import * as React from 'react';
import { History } from 'history';
import { Loader } from 'Src/components/Loader/Loader';
import {
  createSuperAdminApp,
  getSuperAdminApp,
  SuperAdminAppSaveData,
  SuperAdminAppVersion,
  updateSuperAdminApp
} from 'Src/helpers/service/superadmin/apps';

const PLATFORM_OPTIONS = [
  { value: 'IOS', label: 'iOS' },
  { value: 'ANDROID', label: 'Android' },
  { value: 'WEB', label: 'Web' },
  { value: 'OTHER', label: 'Other' }
];

interface Props {
  history: History;
  match: {
    params: {
      appId?: string;
    };
  };
}

interface AppDraft {
  name: string;
  platform: string;
  currentVersion: string;
  minimalVersion: string;
  lowerCurrentVersionText: string;
  lowerMinimalVersionText: string;
  requestRatingThresholdLaunchCount: string;
}

interface State {
  isLoading: boolean;
  isSaving: boolean;
  errorMessage: string;
  draft: AppDraft;
}

function getDefaultDraft(): AppDraft {
  return {
    name: '',
    platform: 'OTHER',
    currentVersion: '',
    minimalVersion: '',
    lowerCurrentVersionText: '',
    lowerMinimalVersionText: '',
    requestRatingThresholdLaunchCount: ''
  };
}

function buildDraftFromApp(app: SuperAdminAppVersion): AppDraft {
  return {
    name: app.name || '',
    platform: app.platform || 'OTHER',
    currentVersion: app.currentVersion || '',
    minimalVersion: app.minimalVersion || '',
    lowerCurrentVersionText: app.lowerCurrentVersionText || '',
    lowerMinimalVersionText: app.lowerMinimalVersionText || '',
    requestRatingThresholdLaunchCount:
      typeof app.requestRatingThresholdLaunchCount === 'number' ? String(app.requestRatingThresholdLaunchCount) : ''
  };
}

function buildPayload(draft: AppDraft): SuperAdminAppSaveData {
  const payload: SuperAdminAppSaveData = {
    name: draft.name.trim(),
    platform: draft.platform,
    currentVersion: draft.currentVersion.trim(),
    minimalVersion: draft.minimalVersion.trim(),
    lowerCurrentVersionText: draft.lowerCurrentVersionText.trim(),
    lowerMinimalVersionText: draft.lowerMinimalVersionText.trim()
  };

  if (draft.requestRatingThresholdLaunchCount.trim() !== '') {
    payload.requestRatingThresholdLaunchCount = Number(draft.requestRatingThresholdLaunchCount);
  }

  return payload;
}

export class SuperAdminAppForm extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      isLoading: false,
      isSaving: false,
      errorMessage: '',
      draft: getDefaultDraft()
    };
  }

  componentDidMount() {
    const appId = this.getAppId();

    if (appId) {
      this.setState({ isLoading: true, errorMessage: '' });

      getSuperAdminApp(appId)
        .then(app => {
          this.setState({
            isLoading: false,
            draft: buildDraftFromApp(app)
          });
        })
        .catch(error => {
          console.error('Can not load application', error);
          this.setState({
            isLoading: false,
            errorMessage: 'Can not load application. Please check the session and try again.'
          });
        });
    }
  }

  getAppId(): string | undefined {
    return this.props.match.params.appId;
  }

  isEditMode(): boolean {
    return typeof this.getAppId() !== 'undefined';
  }

  onInputChange = (field: keyof AppDraft, event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const value = event.target.value;

    this.setState(prevState => ({
      draft: {
        ...prevState.draft,
        [field]: value
      }
    }));
  };

  onTextAreaChange = (field: keyof AppDraft, event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value;

    this.setState(prevState => ({
      draft: {
        ...prevState.draft,
        [field]: value
      }
    }));
  };

  onBackClick = () => {
    this.props.history.push('/apps');
  };

  validate(): string {
    const { draft } = this.state;

    if (draft.name.trim() === '') {
      return 'Application name is required.';
    }

    if (draft.platform.trim() === '') {
      return 'Platform is required.';
    }

    if (
      draft.requestRatingThresholdLaunchCount.trim() !== '' &&
      isNaN(Number(draft.requestRatingThresholdLaunchCount))
    ) {
      return 'Rating launch count should be a number.';
    }

    if (draft.requestRatingThresholdLaunchCount.trim() !== '' && Number(draft.requestRatingThresholdLaunchCount) < 0) {
      return 'Rating launch count should not be negative.';
    }

    return '';
  }

  onSaveClick = () => {
    const validationError = this.validate();

    if (validationError) {
      this.setState({ errorMessage: validationError });
      return;
    }

    const appId = this.getAppId();
    const payload = buildPayload(this.state.draft);

    this.setState({ isSaving: true, errorMessage: '' });

    const savePromise = this.isEditMode() && appId ? updateSuperAdminApp(appId, payload) : createSuperAdminApp(payload);

    savePromise
      .then(() => {
        this.props.history.push('/apps');
      })
      .catch(error => {
        console.error('Can not save application', error);
        this.setState({
          isSaving: false,
          errorMessage: 'Can not save application. Please check the fields and try again.'
        });
      });
  };

  render() {
    const { draft, errorMessage, isLoading, isSaving } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    return (
      <div className="container-fluid py-4">
        <div style={{ maxWidth: 980, margin: '0 auto' }}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <h2 className="mb-1">{this.isEditMode() ? 'Edit application' : 'Add application'}</h2>
              <div className="text-muted">Mobile application version and outdated-app messages.</div>
            </div>
            <button className="btn btn-outline-secondary" onClick={this.onBackClick} disabled={isSaving}>
              Back to applications
            </button>
          </div>

          {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}

          <div className="card">
            <div className="card-body">
              <div className="row">
                <div className="col-md-5">
                  <div className="form-group">
                    <label>Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={draft.name}
                      onChange={event => this.onInputChange('name', event)}
                      placeholder="SquadInTouch"
                    />
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="form-group">
                    <label>Platform</label>
                    <select
                      className="form-control"
                      value={draft.platform}
                      onChange={event => this.onInputChange('platform', event)}
                    >
                      {PLATFORM_OPTIONS.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-3">
                  <div className="form-group">
                    <label>Current version</label>
                    <input
                      type="text"
                      className="form-control"
                      value={draft.currentVersion}
                      onChange={event => this.onInputChange('currentVersion', event)}
                      placeholder="2.2.14"
                    />
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="form-group">
                    <label>Minimal version</label>
                    <input
                      type="text"
                      className="form-control"
                      value={draft.minimalVersion}
                      onChange={event => this.onInputChange('minimalVersion', event)}
                      placeholder="2.0.0"
                    />
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="form-group">
                    <label>Rating launch count</label>
                    <input
                      type="text"
                      className="form-control"
                      value={draft.requestRatingThresholdLaunchCount}
                      onChange={event => this.onInputChange('requestRatingThresholdLaunchCount', event)}
                      placeholder="5"
                    />
                  </div>
                </div>
              </div>

              <hr />

              <h5>Outdated app messages</h5>

              <div className="form-group">
                <label>Lower current version text</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={draft.lowerCurrentVersionText}
                  onChange={event => this.onTextAreaChange('lowerCurrentVersionText', event)}
                  placeholder="Message shown when the user is below the current recommended version"
                />
              </div>

              <div className="form-group">
                <label>Lower minimal version text</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={draft.lowerMinimalVersionText}
                  onChange={event => this.onTextAreaChange('lowerMinimalVersionText', event)}
                  placeholder="Message shown when the user is below the minimum supported version"
                />
              </div>

              <div className="d-flex justify-content-end">
                <button className="btn btn-outline-secondary mr-2" onClick={this.onBackClick} disabled={isSaving}>
                  Cancel
                </button>
                <button className="btn btn-primary" onClick={this.onSaveClick} disabled={isSaving}>
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
